import axios from 'axios'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token')
      // Could trigger a redirect to login page here
    }

    return Promise.reject(error)
  }
)

// Client API endpoints
export const clientsApi = {
  // Get paginated list of clients
  getClients: (params = {}) =>
    api.get('/api/clients/', { params }).then(res => res.data),

  // Get specific client by ID
  getClient: (id) =>
    api.get(`/api/clients/${id}`).then(res => res.data),

  // Create new client
  createClient: (clientData) =>
    api.post('/api/clients/', clientData).then(res => res.data),

  // Update existing client
  updateClient: (id, clientData) =>
    api.put(`/api/clients/${id}`, clientData).then(res => res.data),

  // Delete client (soft delete)
  deleteClient: (id) =>
    api.delete(`/api/clients/${id}`).then(res => res.data),

  // Get client statistics
  getClientStats: (id) =>
    api.get(`/api/clients/${id}/stats`).then(res => res.data),

  // Search client by CUIT
  getClientByCuit: (cuit) =>
    api.get(`/api/clients/search/by-cuit/${cuit}`).then(res => res.data),

  // Get client devices
  getClientDevices: (id, activeOnly = false) =>
    api.get(`/api/clients/${id}/devices`, { params: { active_only: activeOnly } }).then(res => res.data),

  // Get device reports
  getDeviceReports: (clientId, deviceId) =>
    api.get(`/api/clients/${clientId}/devices/${deviceId}/reports`).then(res => res.data),

  // Get billing period reports summary
  getBillingPeriodReports: (clientId, fechaInicio, fechaFin) =>
    api.get(`/api/clients/${clientId}/billing-period-reports`, {
      params: {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }
    }).then(res => res.data)
}

// Logs API endpoints
export const logsApi = {
  // Get logs with filters
  getLogs: (params = {}) =>
    api.get('/api/logs', { params }).then(res => res.data),

  // Get metrics
  getMetrics: (params = {}) =>
    api.get('/api/metrics', { params }).then(res => res.data),

  // Get endpoint calls
  getEndpointCalls: (params = {}) =>
    api.get('/api/endpoint-calls', { params }).then(res => res.data),

  // Get log files
  getLogFiles: () =>
    api.get('/api/files').then(res => res.data)
}

// Script Execution API endpoints
export const scriptsApi = {
  // Get all script executions with optional filters
  getScripts: (params = {}) =>
    api.get('/api/scripts/', { params }).then(res => res.data),

  // Get specific script execution by ID (with log previews)
  getScript: (id) =>
    api.get(`/api/scripts/${id}`).then(res => res.data),

  // Create and start a new script execution
  createScript: (scriptData) =>
    api.post('/api/scripts/', scriptData).then(res => res.data),

  // Update script execution configuration (must be stopped)
  updateScript: (id, scriptData) =>
    api.patch(`/api/scripts/${id}`, scriptData).then(res => res.data),

  // Stop a running script
  stopScript: (id, force = false) =>
    api.post(`/api/scripts/${id}/stop`, null, { params: { force } }).then(res => res.data),

  // Restart a script execution
  restartScript: (id) =>
    api.post(`/api/scripts/${id}/restart`).then(res => res.data),

  // Delete a script execution
  deleteScript: (id) =>
    api.delete(`/api/scripts/${id}`).then(res => res.data),

  // Get script logs
  getLogs: (id, logType = 'stdout', lines = 50) =>
    api.get(`/api/scripts/${id}/logs`, { params: { log_type: logType, lines } }).then(res => res.data),

  // Get process status (CPU, memory, etc)
  getProcessStatus: (id) =>
    api.get(`/api/scripts/${id}/status`).then(res => res.data),

  // Parse logs to extract device information
  parseLogs: (id, logType = 'stdout') =>
    api.post(`/api/scripts/${id}/parse-logs`, null, { params: { log_type: logType } }).then(res => res.data),

  // Get device reports for a script
  getDeviceReports: (id) =>
    api.get(`/api/scripts/${id}/device-reports`).then(res => res.data)
}

// System API endpoints
export const systemApi = {
  // Health check
  healthCheck: () =>
    api.get('/health').then(res => res.data),

  // System status
  getSystemStatus: () =>
    api.get('/api/system/status').then(res => res.data)
}

// Authentication API endpoints
export const authApi = {
  // Login with email and password
  login: (email, password) =>
    api.post('/api/auth/login', { email, password }).then(res => res.data),

  // Get current authenticated user
  getCurrentUser: () =>
    api.get('/api/auth/me').then(res => res.data),

  // Logout (client-side token removal)
  logout: () =>
    api.post('/api/auth/logout').then(res => res.data)
}

// Roles API endpoints
export const rolesApi = {
  // Get paginated list of roles
  getRoles: (params = {}) =>
    api.get('/api/roles/', { params }).then(res => res.data),

  // Get specific role by ID
  getRole: (id, includeDeleted = false) =>
    api.get(`/api/roles/${id}`, { params: { include_deleted: includeDeleted } }).then(res => res.data),

  // Create new role
  createRole: (roleData) =>
    api.post('/api/roles/', roleData).then(res => res.data),

  // Update existing role
  updateRole: (id, roleData) =>
    api.put(`/api/roles/${id}`, roleData).then(res => res.data),

  // Delete role (soft delete)
  deleteRole: (id) =>
    api.delete(`/api/roles/${id}`).then(res => res.data),

  // Restore soft deleted role
  restoreRole: (id) =>
    api.post(`/api/roles/${id}/restore`).then(res => res.data),

  // Permanently delete role
  hardDeleteRole: (id) =>
    api.delete(`/api/roles/${id}/permanent`).then(res => res.data),

  // Search role by name
  getRoleByName: (name, includeDeleted = false) =>
    api.get(`/api/roles/search/by-name/${name}`, { params: { include_deleted: includeDeleted } }).then(res => res.data)
}

// Users API endpoints
export const usersApi = {
  // Get paginated list of users
  getUsers: (params = {}) =>
    api.get('/api/users/', { params }).then(res => res.data),

  // Get specific user by ID
  getUser: (id, includeDeleted = false) =>
    api.get(`/api/users/${id}`, { params: { include_deleted: includeDeleted } }).then(res => res.data),

  // Create new user
  createUser: (userData) =>
    api.post('/api/users/', userData).then(res => res.data),

  // Update existing user
  updateUser: (id, userData) =>
    api.put(`/api/users/${id}`, userData).then(res => res.data),

  // Delete user (soft delete)
  deleteUser: (id) =>
    api.delete(`/api/users/${id}`).then(res => res.data),

  // Restore soft deleted user
  restoreUser: (id) =>
    api.post(`/api/users/${id}/restore`).then(res => res.data),

  // Permanently delete user
  hardDeleteUser: (id) =>
    api.delete(`/api/users/${id}/permanent`).then(res => res.data),

  // Change user password
  changePassword: (id, passwordData) =>
    api.post(`/api/users/${id}/change-password`, passwordData).then(res => res.data),

  // Generate API token for user
  generateToken: (id, expiresDays = 30) =>
    api.post(`/api/users/${id}/generate-token`, null, { params: { expires_days: expiresDays } }).then(res => res.data),

  // Revoke user's API token
  revokeToken: (id) =>
    api.delete(`/api/users/${id}/revoke-token`).then(res => res.data),

  // Search user by email
  getUserByEmail: (email, includeDeleted = false) =>
    api.get(`/api/users/search/by-email/${email}`, { params: { include_deleted: includeDeleted } }).then(res => res.data)
}

// Export default api instance for custom requests
export default api